import React, { useEffect, useRef, useState } from "react";
import {
  listPurchases,
  listSuppliers,
  voidPurchase,
} from "../../services/purchaseService";
import {
  calculatePurchaseSummary,
  getPurchaseDateRange,
} from "../../utils/purchaseReceiving";
import { useToast } from "../../contexts/ToastContext";

const formatCurrency = (value) =>
  Number(value || 0).toLocaleString("en-US", { maximumFractionDigits: 2 });

const errorMessage = (error, fallback) =>
  error?.response?.data?.error || fallback;

const containDialogFocus = (event, container, onClose, canClose = true) => {
  if (event.key === "Escape" && canClose) {
    event.preventDefault();
    onClose();
    return;
  }
  if (event.key !== "Tab") return;
  const focusable = Array.from(
    container?.querySelectorAll(
      'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
    ) || [],
  );
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (!first) {
    event.preventDefault();
  } else if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
};

function PurchaseDetailsDialog({ purchase, onClose, onVoid }) {
  const closeRef = useRef(null);
  const dialogRef = useRef(null);
  useEffect(() => closeRef.current?.focus(), []);
  return (
    <div className="modal-overlay purchase-details-overlay">
      <div
        ref={dialogRef}
        className="modal modal-lg purchase-details-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="purchase-details-title"
        onKeyDown={(event) => containDialogFocus(event, dialogRef.current, onClose)}
      >
        <div className="modal-header">
          <div>
            <p className="eyebrow">PURCHASE DETAILS</p>
            <h2 id="purchase-details-title">{purchase.purchaseNumber}</h2>
            <p>{purchase.supplier?.companyName || "Unknown supplier"}</p>
          </div>
          <button ref={closeRef} type="button" className="close-btn" onClick={onClose} aria-label="Close purchase details">&times;</button>
        </div>
        <div className="purchase-details-body">
          <dl className="purchase-details-summary">
            <div><dt>Date &amp; time</dt><dd>{new Date(purchase.purchasedAt).toLocaleString()}</dd></div>
            <div><dt>Status</dt><dd>{purchase.status === "voided" ? "Voided" : "Completed"}</dd></div>
            <div><dt>Total purchased</dt><dd>MMK {formatCurrency(purchase.totalAmount)}</dd></div>
            <div><dt>Recorded by</dt><dd>Recorded by administrator</dd></div>
          </dl>

          <h3>Received items</h3>
          <div className="purchase-detail-lines">
            {(purchase.items || []).map((item) => (
              <article className="purchase-detail-line" key={item.id || `${item.itemName}-${item.barcode}`}>
                <header>
                  <div><strong>{item.itemName}</strong><code>{item.barcode || "No barcode"}</code></div>
                  <strong>MMK {formatCurrency(item.lineTotal)}</strong>
                </header>
                <dl>
                  <div><dt>Receiving unit</dt><dd>{item.unitName}</dd></div>
                  <div><dt>Quantity</dt><dd>{item.packageQuantity} packages</dd></div>
                  <div><dt>Conversion</dt><dd>1 {item.unitName} = {item.conversionFactor} base units</dd></div>
                  <div><dt>Stock received</dt><dd>{item.receivedBaseQuantity} base units received</dd></div>
                  <div><dt>Buying price / {item.unitName}</dt><dd>MMK {formatCurrency(item.unitBuyPrice)}</dd></div>
                  <div><dt>Selling price / {item.unitName}</dt><dd>MMK {formatCurrency(item.selectedUnitSellPrice)}</dd></div>
                  <div><dt>Base buying price</dt><dd>MMK {formatCurrency(item.appliedBaseBuyPrice)}</dd></div>
                  <div><dt>Base selling price</dt><dd>MMK {formatCurrency(item.appliedBaseSellPrice)}</dd></div>
                </dl>
              </article>
            ))}
          </div>

          {purchase.status === "voided" && (
            <div className="purchase-void-record">
              <strong>Voided {purchase.voidedAt ? new Date(purchase.voidedAt).toLocaleString() : ""}</strong>
              <p>{purchase.voidReason}</p>
            </div>
          )}
        </div>
        <div className="modal-footer">
          {purchase.status === "completed" && (
            <button type="button" className="btn btn-danger" onClick={() => onVoid(purchase)}>Void purchase</button>
          )}
          <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

function VoidPurchaseDialog({ purchase, onCancel, onSuccess }) {
  const showToast = useToast();
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [blockedItems, setBlockedItems] = useState([]);
  const reasonRef = useRef(null);
  const dialogRef = useRef(null);
  useEffect(() => reasonRef.current?.focus(), []);

  const confirmVoid = async () => {
    const cleanReason = reason.trim();
    if (!cleanReason || saving) return;
    setSaving(true);
    setBlockedItems([]);
    try {
      await voidPurchase({ purchaseId: purchase.id, reason: cleanReason });
      onSuccess(cleanReason);
    } catch (error) {
      const items = error?.response?.data?.details?.items;
      setBlockedItems(Array.isArray(items) ? items : []);
      showToast(
        errorMessage(error, "The purchase could not be voided. No stock was changed."),
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay purchase-void-overlay">
      <div
        ref={dialogRef}
        className="modal purchase-void-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="void-purchase-title"
        onKeyDown={(event) =>
          containDialogFocus(event, dialogRef.current, onCancel, !saving)
        }
      >
        <div className="modal-header">
          <div>
            <p className="eyebrow">ADMINISTRATOR ACTION</p>
            <h2 id="void-purchase-title">Void {purchase.purchaseNumber}?</h2>
          </div>
        </div>
        <div className="purchase-void-body">
          <p>This reverses the received stock when enough stock remains. A voided purchase cannot be edited or restored.</p>
          <p>စတော့လက်ကျန် လုံလောက်မှသာ ပြန်နုတ်ပါမည်။ Void လုပ်ပြီးပါက ပြန်ပြင်၍ မရပါ။</p>
          <div className="form-group">
            <label htmlFor="purchase-void-reason">Reason *</label>
            <textarea
              ref={reasonRef}
              id="purchase-void-reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              rows="3"
              placeholder="Why is this purchase being voided?"
            />
            <small>ပယ်ဖျက်ရသည့် အကြောင်းရင်းကို ရေးပါ။</small>
          </div>
          {blockedItems.length > 0 && (
            <div className="purchase-void-blocked" role="alert">
              <strong>Not enough stock remains for:</strong>
              <ul>{blockedItems.map((item) => <li key={item}>{item}</li>)}</ul>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={saving}>Cancel</button>
          <button type="button" className="btn btn-danger" data-confirm-void onClick={confirmVoid} disabled={!reason.trim() || saving}>
            {saving ? "Voiding..." : "Confirm void"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function PurchaseHistoryView({ onInventoryChanged = () => {} }) {
  const showToast = useToast();
  const [scope, setScope] = useState("today");
  const [supplierId, setSupplierId] = useState("");
  const [status, setStatus] = useState("");
  const [suppliers, setSuppliers] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [voidTarget, setVoidTarget] = useState(null);

  useEffect(() => {
    listSuppliers({ includeInactive: true })
      .then(setSuppliers)
      .catch(() => setSuppliers([]));
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    const filters = {
      ...getPurchaseDateRange(scope),
      ...(supplierId ? { supplierId: Number(supplierId) } : {}),
      ...(status ? { status } : {}),
    };
    listPurchases(filters)
      .then((result) => {
        if (active) setPurchases(result || []);
      })
      .catch((error) => {
        if (active) showToast(errorMessage(error, "Purchases could not be loaded."), "error");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [scope, supplierId, status, showToast]);

  const summary = calculatePurchaseSummary(purchases);
  const startVoid = (purchase) => {
    setSelectedPurchase(null);
    setVoidTarget(purchase);
  };
  const finishVoid = (reason) => {
    setPurchases((current) => current.map((purchase) =>
      purchase.id === voidTarget.id
        ? { ...purchase, status: "voided", voidReason: reason, voidedAt: new Date().toISOString() }
        : purchase,
    ));
    const reference = voidTarget.purchaseNumber;
    setVoidTarget(null);
    onInventoryChanged();
    showToast(`Purchase ${reference} was voided.`, "success");
  };

  return (
    <section className="purchase-history" aria-labelledby="purchase-history-title">
      <header className="purchase-view-header">
        <div>
          <p className="eyebrow">PURCHASE REPORTS</p>
          <h2 id="purchase-history-title">Purchase history</h2>
          <p>ဝယ်ယူထားသော စာရင်းနှင့် စတော့လက်ခံမှတ်တမ်း</p>
        </div>
        <div className="purchase-scope-switch" aria-label="Purchase reporting period">
          <button type="button" className={scope === "today" ? "is-active" : ""} data-purchase-scope="today" aria-pressed={scope === "today"} onClick={() => setScope("today")}>Today</button>
          <button type="button" className={scope === "all" ? "is-active" : ""} data-purchase-scope="all" aria-pressed={scope === "all"} onClick={() => setScope("all")}>All</button>
        </div>
      </header>

      <div className="purchase-summary-grid">
        <article data-summary="purchase-count"><span>Completed purchases</span><strong>{summary.purchaseCount}</strong></article>
        <article data-summary="purchase-total"><span>Total purchased</span><strong>MMK {formatCurrency(summary.totalPurchased)}</strong></article>
        <article data-summary="base-units"><span>Base units received</span><strong>{formatCurrency(summary.receivedBaseUnits)}</strong></article>
      </div>

      <div className="purchase-history-filters">
        <label>
          <span>Supplier</span>
          <select name="supplierFilter" value={supplierId} onChange={(event) => setSupplierId(event.target.value)}>
            <option value="">All suppliers</option>
            {suppliers.map((supplier) => <option key={supplier.id} value={supplier.id}>{supplier.companyName}</option>)}
          </select>
        </label>
        <label>
          <span>Status</span>
          <select name="statusFilter" value={status} onChange={(event) => setStatus(event.target.value)}>
            <option value="">All statuses</option>
            <option value="completed">Completed</option>
            <option value="voided">Voided</option>
          </select>
        </label>
      </div>

      {loading ? (
        <p className="purchase-history-loading" role="status">Loading purchases...</p>
      ) : purchases.length === 0 ? (
        <div className="purchase-history-empty"><strong>No purchases in this period</strong><p>ဤကာလအတွင်း ဝယ်ယူမှု မရှိသေးပါ။</p></div>
      ) : (
        <>
          <div className="purchase-history-table-wrap">
            <table className="purchase-history-table">
              <thead><tr><th scope="col">Purchase</th><th scope="col">Date &amp; time</th><th scope="col">Supplier</th><th scope="col">Items</th><th scope="col">Total</th><th scope="col">Status</th><th scope="col">Actions</th></tr></thead>
              <tbody>
                {purchases.map((purchase) => (
                  <tr key={purchase.id}>
                    <td><code>{purchase.purchaseNumber}</code></td>
                    <td>{new Date(purchase.purchasedAt).toLocaleString()}</td>
                    <td>{purchase.supplier?.companyName || "—"}</td>
                    <td>{purchase.items?.length || 0}</td>
                    <td>MMK {formatCurrency(purchase.totalAmount)}</td>
                    <td><span data-purchase-status={purchase.id} className={`status-badge is-${purchase.status}`}>{purchase.status === "voided" ? "Voided" : "Completed"}</span></td>
                    <td className="purchase-history-actions">
                      <button type="button" className="table-row-action" data-purchase-details={purchase.id} onClick={() => setSelectedPurchase(purchase)}>View details</button>
                      {purchase.status === "completed" && <button type="button" className="table-row-action is-danger" data-void-purchase={purchase.id} onClick={() => startVoid(purchase)}>Void</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="purchase-history-mobile-list">
            {purchases.map((purchase) => (
              <article key={purchase.id} className="purchase-history-card">
                <header><code>{purchase.purchaseNumber}</code><strong>MMK {formatCurrency(purchase.totalAmount)}</strong></header>
                <p>{purchase.supplier?.companyName || "—"}</p>
                <p>{new Date(purchase.purchasedAt).toLocaleString()}</p>
                <div><span className={`status-badge is-${purchase.status}`}>{purchase.status === "voided" ? "Voided" : "Completed"}</span></div>
                <button type="button" className="table-row-action" onClick={() => setSelectedPurchase(purchase)}>View details</button>
                {purchase.status === "completed" && <button type="button" className="table-row-action is-danger" onClick={() => startVoid(purchase)}>Void</button>}
              </article>
            ))}
          </div>
        </>
      )}

      {selectedPurchase && <PurchaseDetailsDialog purchase={selectedPurchase} onClose={() => setSelectedPurchase(null)} onVoid={startVoid} />}
      {voidTarget && <VoidPurchaseDialog purchase={voidTarget} onCancel={() => setVoidTarget(null)} onSuccess={finishVoid} />}
    </section>
  );
}
